import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {ExternalReferenceEcommercePlatform} from '@myparcel-frontend/order-service';
import type {
  GetProductsData,
  GetProductsResponse,
  PostCreateProductData,
  PostCreateProductResponse,
  PostUpdateProductData,
  PostUpdateProductResponse,
  Product,
} from '@myparcel-frontend/product-service';
import {useProductService, ProductStatus} from '@myparcel-frontend/product-service';
import {storeToRefs} from 'pinia';
import {ref} from 'vue';
import {removeEmpty} from '../../utils/removeEmpty';
import useSalesChannels from '../useSalesChannels';
import useBolEcommerceSalesChannel from '../useSalesChannels/useBolEcommerceSalesChannels';
import useShopifyEcommerceSalesChannel from '../useSalesChannels/useShopifyEcommerceSalesChannels';
import useWoocommerceEcommerceSalesChannels from '../useSalesChannels/useWoocommerceEcommerceSalesChannels';
import useTableRowHighlights from '../useTableRowHighlights';
import useLightspeedEcommerceSalesChannels from '@/composables/useSalesChannels/useLightspeedEcommerceSalesChannels';
import usePrestaEcommerceSalesChannels from '@/composables/useSalesChannels/usePrestaEcommerceSalesChannels';
import useShop from '@/composables/useShop/useShop';
import {useProductStore} from '@/store/productStore/productStore';

const indexedProductIds = ref<string[]>([]);

export const useProducts = () => {
  const {importWoocommerceProducts} = useWoocommerceEcommerceSalesChannels();
  const {importBolProducts} = useBolEcommerceSalesChannel();
  const {importShopifyProducts} = useShopifyEcommerceSalesChannel();
  const {importLightspeedProducts} = useLightspeedEcommerceSalesChannels();
  const {importPrestaProducts} = usePrestaEcommerceSalesChannels();
  const {getProducts, postUpdateProduct, postCreateProduct} = useProductService();
  const {fetchSalesChannelByCurrentShopId, salesChannelsForCurrentShop} = useSalesChannels();
  const productStore = useProductStore();
  const {productQuery, productFilters, productAggregations, products} = storeToRefs(productStore);

  const {currentShop} = useShop();

  const {
    clearRecentlyAddedIds,
    clearRecentlyUpdatedIds,
    setRecentlyAddedId,
    setRecentlyUpdatedId,
    recentlyAddedRowsById,
    recentlyUpdatedRowsById,
  } = useTableRowHighlights('products');

  const editProduct = async (product: Required<PostUpdateProductData>['body']): Promise<PostUpdateProductResponse> => {
    let response: Product;

    try {
      response = await postUpdateProduct(product);
    } catch (error) {
      return Promise.reject(error);
    }

    productStore.upsertProduct(response);

    clearRecentlyAddedIds();

    return response;
  };

  const disableProduct = (product: Required<PostUpdateProductData>['body']) => {
    return editProduct({
      ...product,
      status: ProductStatus.INACTIVE,
    });
  };

  const enableProduct = (product: Required<PostUpdateProductData>['body']) => {
    return editProduct({
      ...product,
      status: ProductStatus.ACTIVE,
    });
  };

  const createProduct = async (
    product: Required<PostCreateProductData>['body'],
  ): Promise<PostCreateProductResponse> => {
    const response = await postCreateProduct(product);

    productStore.addProduct(response);

    // ensure newly added product is shown in the index, even if it was not included in the search results
    if (!indexedProductIds.value.includes(response.id) && response.shopIds.includes(currentShop.value.id.toString())) {
      indexedProductIds.value.unshift(response.id);
    }

    setRecentlyAddedId(response.id);

    return response;
  };

  const searchProducts = async (query?: NonNullable<GetProductsData['query']>): Promise<GetProductsResponse> => {
    const sanitizedQueryParams = removeEmpty<GetProductsData['query']>(query);

    const response = await getProducts(sanitizedQueryParams);

    productStore.upsertProducts(response.results);

    return response;
  };

  const searchComposedProducts = async (productComposition: NonNullable<Product['children']>) => {
    const missingComposedProductIds: string[] = productComposition
      .filter(({id}) => {
        const product = productStore.productById(id);

        return !product;
      })
      .map((product) => product.id);

    if (missingComposedProductIds.length > 0) {
      await searchProducts({
        filter: {
          id: missingComposedProductIds,
        },
      });
    }
  };

  const getProduct = async (id: string): Promise<Product | undefined> => {
    const product = productStore.productById(id);

    if (product) {
      return product;
    }

    const response = await getProducts({
      filter: {
        id: [id],
      },
    });

    productStore.upsertProducts(response.results);
    return response.results[0];
  };

  const findProductByProperties = (properties: (keyof Product)[], value: string): Product | undefined => {
    return Object.values(products.value).find((product) => properties.some((property) => product[property] === value));
  };

  const incrementAndDecrementTabCount = (status: ProductStatus, amount: number) => {
    const activeItems = productAggregations.value?.status?.find((item) => item.key === ProductStatus.ACTIVE);
    const inactiveItems = productAggregations.value?.status?.find((item) => item.key === ProductStatus.INACTIVE);

    if (!activeItems || !inactiveItems) {
      return;
    }

    // Adjust tab counts based on the status
    activeItems.count += status === ProductStatus.ACTIVE ? amount : -amount;
    inactiveItems.count += status === ProductStatus.ACTIVE ? -amount : amount;
  };

  const importProductsFromSalesChannel = async (salesChannelIds: string[]) => {
    if (!salesChannelsForCurrentShop.value.length) {
      await fetchSalesChannelByCurrentShopId();
    }

    // Group sales channels by ecommerce platform
    const groupedChannels = salesChannelsForCurrentShop.value.reduce<Record<string, EcommerceSalesChannel[]>>(
      (acc, channel) => {
        const key = channel.ecommercePlatform;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(channel);
        return acc;
      },
      {},
    );

    const bolSalesChannels = salesChannelIds.filter((id) => groupedChannels.BOL?.some((channel) => channel.id === id));

    const woocommerceSalesChannels = salesChannelIds.filter((id) =>
      groupedChannels.WOOCOMMERCE?.some((channel) => channel.id === id),
    );

    const shopifySalesChannels = salesChannelIds.filter((id) =>
      groupedChannels.SHOPIFY?.some((channel) => channel.id === id),
    );

    const lightspeedSalesChannels = salesChannelIds.filter((id) =>
      groupedChannels.LIGHTSPEED?.some((channel) => channel.id === id),
    );

    const prestaSalesChannels = salesChannelIds.filter((id) =>
      groupedChannels.PRESTA?.some((channel) => channel.id === id),
    );

    const apiCalls = [
      {
        ecommercePlatform: 'BOL' as ExternalReferenceEcommercePlatform['name'],
        importFunction: bolSalesChannels.length ? importBolProducts(bolSalesChannels) : Promise.resolve(),
      },
      {
        ecommercePlatform: 'WOOCOMMERCE' as ExternalReferenceEcommercePlatform['name'],
        importFunction: woocommerceSalesChannels.length
          ? importWoocommerceProducts(woocommerceSalesChannels)
          : Promise.resolve(),
      },
      {
        ecommercePlatform: 'SHOPIFY' as ExternalReferenceEcommercePlatform['name'],
        importFunction: shopifySalesChannels.length ? importShopifyProducts(shopifySalesChannels) : Promise.resolve(),
      },
      {
        ecommercePlatform: 'LIGHTSPEED' as ExternalReferenceEcommercePlatform['name'],
        importFunction: lightspeedSalesChannels.length
          ? importLightspeedProducts(lightspeedSalesChannels)
          : Promise.resolve(),
      },
      {
        ecommercePlatform: 'PRESTA' as ExternalReferenceEcommercePlatform['name'],
        importFunction: prestaSalesChannels.length ? importPrestaProducts(prestaSalesChannels) : Promise.resolve(),
      },
    ];

    const settledResults = await Promise.allSettled(apiCalls.map((call) => call.importFunction));
    const errors: string[] = [];

    settledResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        errors.push(apiCalls[index].ecommercePlatform);
      }
    });
    return {errors};
  };

  const isProductPartOfAnyComposition = (productId: string): boolean => {
    return Object.values(products.value).some((product) => product.children?.some((child) => child.id === productId));
  };

  const isProductWithComposition = (product: Product): boolean => {
    return Array.isArray(product.children) && product.children.length > 0;
  };

  return {
    editProduct,
    createProduct,
    getProduct,
    incrementAndDecrementTabCount,
    upsertProduct: productStore.upsertProduct,
    upsertProducts: productStore.upsertProducts,
    productById: productStore.productById,
    findProductByProperties,
    products,
    indexedProductIds,
    productAggregations,
    productQuery,
    productFilters,
    recentlyAddedProductIds: recentlyAddedRowsById,
    recentlyUpdatedProductIds: recentlyUpdatedRowsById,
    clearRecentlyAddedProductIds: clearRecentlyAddedIds,
    clearRecentlyUpdatedProductIds: clearRecentlyUpdatedIds,
    setRecentlyAddedProductId: setRecentlyAddedId,
    setRecentlyUpdatedProductId: setRecentlyUpdatedId,
    searchProducts,
    disableProduct,
    enableProduct,
    searchComposedProducts,
    importProductsFromSalesChannel,
    isProductPartOfAnyComposition,
    isProductWithComposition,
  };
};
